import type { EngineId } from '@geosoft/viewer-sdk';
import { Button, Chip, Icon, IconButton, Stack, Text, tokens } from '@/design';

export type ShellDrawerId =
  | 'app-launcher'
  | 'search'
  | 'notifications'
  | 'help'
  | 'user-menu'
  | 'compare'
  | 'share'
  | 'audit';

interface ShellHeaderProps {
  engine: EngineId;
  onEngineChange(next: EngineId): void;
  userName: string;
  defenceVariantActive: boolean;
  compareDisabled: boolean;
  shareDisabled: boolean;
  onOpenDrawer(id: ShellDrawerId, opener: HTMLElement | null): void;
}

function openFromEvent(
  id: ShellDrawerId,
  onOpenDrawer: ShellHeaderProps['onOpenDrawer'],
  target: EventTarget | null,
) {
  onOpenDrawer(id, target instanceof HTMLElement ? target : null);
}

export function ShellHeader({
  engine,
  onEngineChange,
  userName,
  defenceVariantActive,
  compareDisabled,
  shareDisabled,
  onOpenDrawer,
}: ShellHeaderProps) {
  const ENGINE_OPTIONS: Array<{ id: EngineId; label: string }> = [
    { id: 'maplibre', label: 'MapLibre (2D)' },
    { id: 'openlayers', label: 'Canvas 2D (no GPU)' },
    { id: 'deckgl', label: 'deck.gl (2D+)' },
    { id: 'cesium', label: 'Cesium (3D)' },
    { id: 'three', label: 'Three.js (BIM)' },
    { id: 'giro3d', label: 'Giro3D (3D)' },
    { id: 'potree', label: 'Potree (Point Cloud)' },
  ];

  const engineLabel = ENGINE_OPTIONS.find((o) => o.id === engine)?.label ?? engine;

  return (
    <header
      role="banner"
      aria-label="GeoViewer header"
      style={{
        display: 'grid',
        gridTemplateRows: 'auto auto',
        borderBottom: tokens.border.thin,
        background: tokens.color.surface[1],
      }}
    >
      <Stack
        direction="row"
        align="center"
        gap={2}
        style={{ padding: tokens.space[2], minHeight: tokens.density.rowComfy }}
      >
        <Text variant="subtitle" as="span">
          GeoSoft GeoViewer
        </Text>

        <span style={{ flex: '1 1 auto' }} />

        <IconButton
          aria-label="Application launcher"
          data-consumed-shell-slot="gvw-006"
          onClick={(event) => openFromEvent('app-launcher', onOpenDrawer, event.currentTarget)}
        >
          <Icon name="apps" size={20} decorative />
        </IconButton>

        <button
          type="button"
          role="combobox"
          aria-label="Global search"
          aria-expanded={false}
          aria-haspopup="listbox"
          data-consumed-shell-slot="gvw-009"
          onClick={(event) => openFromEvent('search', onOpenDrawer, event.currentTarget)}
          style={{
            appearance: 'none',
            border: tokens.border.thin,
            borderRadius: tokens.radius.pill,
            minHeight: tokens.density.touchMin,
            background: tokens.color.surface[2],
            color: tokens.color.ink.secondary,
            paddingInline: tokens.space[3],
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            cursor: 'pointer',
          }}
        >
          <Icon name="search" size={20} decorative />
          <span>Search layers, places, and tools</span>
        </button>

        <button
          type="button"
          aria-label="Active organization"
          aria-haspopup="listbox"
          aria-expanded={false}
          data-consumed-shell-slot="gvw-007"
          onClick={(event) => openFromEvent('user-menu', onOpenDrawer, event.currentTarget)}
          style={{
            appearance: 'none',
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            minHeight: tokens.density.touchMin,
            background: tokens.color.surface[2],
            color: tokens.color.ink.primary,
            paddingInline: tokens.space[2],
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            cursor: 'pointer',
          }}
        >
          <Icon name="business" size={20} decorative />
          <span>Org selector</span>
        </button>

        {defenceVariantActive ? (
          <Chip
            icon="policy"
            label="Defence variant"
            variant="tertiary"
            aria-label="Defence variant"
          />
        ) : null}

        <Button
          variant="ghost"
          aria-label="Compare scenarios"
          disabled={compareDisabled}
          onClick={(event) => openFromEvent('compare', onOpenDrawer, event.currentTarget)}
        >
          <Icon name="compare_arrows" size={20} decorative />
          <span>Compare</span>
        </Button>

        <Button
          variant="ghost"
          aria-label="Share this view"
          disabled={shareDisabled}
          onClick={(event) => openFromEvent('share', onOpenDrawer, event.currentTarget)}
        >
          <Icon name="share" size={20} decorative />
          <span>Share</span>
        </Button>

        <IconButton
          aria-label="Notifications"
          data-consumed-shell-slot="gvw-011"
          onClick={(event) => openFromEvent('notifications', onOpenDrawer, event.currentTarget)}
        >
          <Icon name="notifications" size={20} decorative />
        </IconButton>

        <IconButton
          aria-label="Help"
          data-consumed-shell-slot="gvw-015"
          onClick={(event) => openFromEvent('help', onOpenDrawer, event.currentTarget)}
        >
          <Icon name="help" size={20} decorative />
        </IconButton>

        <IconButton
          aria-label={`Account menu, signed in as ${userName}`}
          data-consumed-shell-slot="gvw-013"
          onClick={(event) => openFromEvent('user-menu', onOpenDrawer, event.currentTarget)}
        >
          <Icon name="account_circle" size={20} decorative />
        </IconButton>
      </Stack>

      <Stack
        direction="row"
        align="center"
        gap={2}
        style={{
          minHeight: tokens.density.rowDefault,
          paddingInline: tokens.space[2],
          borderTop: tokens.border.thin,
        }}
      >
        <nav aria-label="Breadcrumb" data-consumed-shell-slot="gvw-016">
          <ol
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              color: tokens.color.ink.secondary,
            }}
          >
            <li>Tenant</li>
            <li aria-hidden>
              <Icon name="chevron_right" size={16} decorative />
            </li>
            <li>GeoViewer</li>
            <li aria-hidden>
              <Icon name="chevron_right" size={16} decorative />
            </li>
            <li aria-current="page">{engineLabel}</li>
          </ol>
        </nav>

        <span style={{ flex: '1 1 auto' }} />

        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            color: tokens.color.ink.secondary,
          }}
        >
          <span>Engine</span>
          <select
            aria-label="Rendering engine"
            value={engine}
            onChange={(event) => onEngineChange(event.target.value as EngineId)}
            style={{
              minHeight: tokens.density.touchMin,
              borderRadius: tokens.radius.input,
              border: tokens.border.thin,
              background: tokens.color.surface[2],
              color: tokens.color.ink.primary,
              paddingInline: tokens.space[2],
            }}
          >
            {ENGINE_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </Stack>
    </header>
  );
}
